import React from 'react';
import { CardFinish, FINISH_LABELS, FINISH_ORDER } from 'utils/finishes';
import './FinishSelect.css';

export interface FinishSelectProps {
  value: CardFinish;
  /** Finishes the printing exists in AND the recipient accepts (offerableFinishes) */
  offerable: readonly CardFinish[];
  onChange: (finish: CardFinish) => void;
  /**
   * Points per finish where the surface knows them, so a wrong pick is visible
   * before it costs points (D-3). Missing entries render the label alone.
   */
  pointsByFinish?: Partial<Record<CardFinish, number | null | undefined>>;
  /** The finish the item had when editing began; drives the delta line (D-9) */
  baselineFinish?: CardFinish;
  disabled?: boolean;
  /** Class for the <select> (each surface keeps its own select styling) */
  selectClassName?: string;
  /** Class for the static single-finish text */
  staticClassName?: string;
  ariaLabel?: string;
}

/**
 * The offer / send finish control (Design Pass D-3 and D-9): a dropdown that
 * carries each finish's price when two or more finishes are offerable, a static
 * label when exactly one is. The old `canOfferFoil && canOfferNonFoil` gate hid
 * the choice on every foil + etched card; the rule is now "two or more".
 *
 * Points inside an <option> are plain text because an <option> cannot host the
 * branded Points component; the row's own price cell keeps the coin.
 */
export const FinishSelect: React.FC<FinishSelectProps> = ({
  value,
  offerable,
  onChange,
  pointsByFinish = {},
  baselineFinish,
  disabled = false,
  selectClassName = '',
  staticClassName = '',
  ariaLabel = 'Finish',
}) => {
  const ordered = FINISH_ORDER.filter((f) => offerable.includes(f));
  const points = (f: CardFinish): number | null | undefined => pointsByFinish[f];

  if (ordered.length < 2) {
    const only = ordered[0] ?? value;
    return (
      <span className={staticClassName} data-finish={only}>
        {FINISH_LABELS[only]}
      </span>
    );
  }

  const base = baselineFinish ?? value;
  const from = points(base);
  const to = points(value);
  const delta = from != null && to != null && value !== base ? to - from : null;

  return (
    <span className="finish-select">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as CardFinish)}
        className={selectClassName}
        disabled={disabled}
        aria-label={ariaLabel}
        data-testid="finish-select"
      >
        {ordered.map((f) => {
          const p = points(f);
          return (
            <option key={f} value={f}>
              {FINISH_LABELS[f]}
              {p != null ? ` · ${p.toLocaleString()} pts` : ''}
            </option>
          );
        })}
      </select>
      {delta !== null && delta !== 0 && (
        <span className={`finish-select__delta ${delta < 0 ? 'lowers' : 'raises'}`} data-testid="finish-delta">
          Switching to {FINISH_LABELS[value]} {delta < 0 ? 'lowers' : 'raises'} this item by{' '}
          {Math.abs(delta).toLocaleString()} points
        </span>
      )}
    </span>
  );
};

export default FinishSelect;
